import React, { useRef, useEffect, useState } from 'react'
import { Group, Text, Rect, Image, Transformer } from 'react-konva'
import { isQrField, isLogoField, enforcePhotoRatio } from './canvasUtils'
import placeholderImage from '../../../assets/placeholder_photo.jpg'
import schoolLogoImage from '../../../assets/school_logo.png'

/**
 * TransformableImage - A draggable, resizable image placeholder for the canvas
 * Photo fields default to portrait (8x10). Set field.horizontal for landscape (10x8).
 * QR code fields maintain square aspect ratio.
 */
export default function TransformableImage({
  field,
  isSelected,
  onSelect,
  onChange,
  onContextMenu,
  onDragStart,
  onDragMove,
  registerRef,
  hideBoundingBoxes
}) {
  const groupRef = useRef()
  const trRef = useRef()
  const [portraitImage, setPortraitImage] = useState(null)
  const [landscapeImage, setLandscapeImage] = useState(null)
  const [logoImage, setLogoImage] = useState(null)

  const isQr = isQrField(field)
  const isLogo = isLogoField(field)
  const isHorizontal = field.horizontal || false

  // Load logo placeholder for logo fields
  useEffect(() => {
    if (!isLogo) return
    const img = new window.Image()
    img.src = schoolLogoImage
    img.onload = () => setLogoImage(img)
  }, [isLogo])

  // Load and crop placeholder into portrait (4:5) and landscape (5:4) versions
  useEffect(() => {
    if (isQr || isLogo) return
    const img = new window.Image()
    img.src = placeholderImage
    img.onload = () => {
      const w = img.naturalWidth
      const h = img.naturalHeight

      // Portrait crop: 4:5 ratio, centered
      const pCanvas = document.createElement('canvas')
      const pW = Math.min(w, Math.round(h * 4 / 5))
      const pH = Math.round(pW * 5 / 4)
      pCanvas.width = pW
      pCanvas.height = pH
      const pCtx = pCanvas.getContext('2d')
      pCtx.drawImage(img, (w - pW) / 2, (h - pH) / 2, pW, pH, 0, 0, pW, pH)
      const pImg = new window.Image()
      pImg.src = pCanvas.toDataURL()
      pImg.onload = () => setPortraitImage(pImg)

      // Landscape crop: 5:4 ratio, take top portion
      const lCanvas = document.createElement('canvas')
      const lW = Math.min(w, Math.round(h * 5 / 4))
      const lH = Math.round(lW * 4 / 5)
      lCanvas.width = lW
      lCanvas.height = lH
      const lCtx = lCanvas.getContext('2d')
      lCtx.drawImage(img, (w - lW) / 2, 0, lW, lH, 0, 0, lW, lH)
      const lImg = new window.Image()
      lImg.src = lCanvas.toDataURL()
      lImg.onload = () => setLandscapeImage(lImg)
    }
  }, [isQr, isLogo])

  useEffect(() => {
    if (isSelected && trRef.current && groupRef.current) {
      trRef.current.nodes([groupRef.current])
      trRef.current.getLayer().batchDraw()
    }
  }, [isSelected])

  // Register group ref with parent for group move
  useEffect(() => {
    if (registerRef) registerRef(groupRef.current)
    return () => { if (registerRef) registerRef(null) }
  }, [registerRef])

  const rawWidth = field.width || 80

  // Enforce aspect ratio: 4:5 / 5:4 for photos, 1:1 for QR codes and logos
  let width, height
  if (isQr || isLogo) {
    const side = Math.min(rawWidth, field.height || 80)
    width = side
    height = side
  } else {
    const corrected = enforcePhotoRatio(rawWidth, isHorizontal)
    width = corrected.width
    height = corrected.height
  }

  const placeholderImg = isLogo ? logoImage : (isHorizontal ? landscapeImage : portraitImage)

  return (
    <>
      <Group
        ref={groupRef}
        x={field.left || 0}
        y={field.top || 0}
        width={width}
        height={height}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onContextMenu={onContextMenu}
        onDragStart={(e) => {
          if (onDragStart) onDragStart(field.id)
        }}
        onDragMove={(e) => {
          if (onDragMove) {
            const pos = { x: e.target.x(), y: e.target.y() }
            const snapped = onDragMove(field.id, pos)
            if (snapped) {
              e.target.x(snapped.x)
              e.target.y(snapped.y)
            }
          }
        }}
        onDragEnd={(e) => {
          onChange({
            ...field,
            left: Math.round(e.target.x()),
            top: Math.round(e.target.y()),
          })
        }}
        onTransformEnd={(e) => {
          const node = groupRef.current
          const scaleX = node.scaleX()
          const scaleY = node.scaleY()

          node.scaleX(1)
          node.scaleY(1)

          let newWidth = Math.round(Math.max(30, width * scaleX))

          // Re-enforce aspect ratio after resize
          if (isQr || isLogo) {
            const side = Math.max(newWidth, Math.round(Math.max(30, height * scaleY)))
            newWidth = side
          }
          const corrected = (isQr || isLogo)
            ? { width: newWidth, height: newWidth }
            : enforcePhotoRatio(newWidth, isHorizontal)

          onChange({
            ...field,
            left: Math.round(node.x()),
            top: Math.round(node.y()),
            width: corrected.width,
            height: corrected.height,
          })
        }}
      >
        {/* Placeholder content */}
        {isQr ? (
          <Rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="rgba(156, 39, 176, 0.15)"
            stroke="#9C27B0"
            strokeWidth={2}
          />
        ) : placeholderImg ? (
          <Image
            x={0}
            y={0}
            width={width}
            height={height}
            image={placeholderImg}
            opacity={0.5}
          />
        ) : (
          <Rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="rgba(74, 144, 217, 0.15)"
            stroke="#4a90d9"
            strokeWidth={2}
          />
        )}
        {/* Border */}
        <Rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="transparent"
          stroke={isSelected ? '#4a90d9' : 'rgba(0, 0, 0, 0.4)'}
          strokeWidth={isSelected ? 2 : 1}
          dash={isSelected ? [] : [4, 4]}
        />
        {/* Label */}
        <Text
          x={4}
          y={4}
          text={field.label || 'Image'}
          fontSize={Math.min(12, Math.max(8, width / 10))}
          fill={isSelected ? '#4a90d9' : '#333333'}
          fontStyle="bold"
          shadowColor="#ffffff"
          shadowBlur={2}
          shadowOpacity={0.9}
        />
      </Group>
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 30 || newBox.height < 30) {
              return oldBox
            }
            return newBox
          }}
          anchorSize={8}
          anchorCornerRadius={1}
          anchorStroke="#4a90d9"
          anchorFill="#ffffff"
          borderStroke="#4a90d9"
          borderStrokeWidth={1}
          enabledAnchors={['top-left', 'top-right', 'bottom-right', 'bottom-left']}
          rotateEnabled={false}
          keepRatio={true}
        />
      )}
    </>
  )
}
